import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, useChatContext } from 'stream-chat-react';
import type { UserResponse } from 'stream-chat';
import _debounce from 'lodash/debounce';

import './CreateChannel.css';

import { CloseOutlined, SearchOutlined, LoadingOutlined } from '@ant-design/icons';

type Props = {
  onClose: () => void;
  toggleMobile: () => void;
  filter: string;
};

const CreateChannel: React.FC<Props> = (props) => {
  const { onClose, filter } = props;
  const { client, setActiveChannel } = useChatContext();

  const [focusedUser, setFocusedUser] = useState<number>();
  const [inputText, setInputText] = useState('');
  const [resultsOpen, setResultsOpen] = useState(false);
  const [searchEmpty, setSearchEmpty] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserResponse>();
  const [users, setUsers] = useState<UserResponse[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);

  const findUsers = async () => {
    if (searching) return;
    setSearching(true);

    try {
      const response = await client.queryUsers(
        {
          id: { $ne: client.userID as string },
          name: { $autocomplete: inputText },
          teams: { $contains: filter },
        } as any,
        { id: 1 },
        { limit: 6 }
      );

      if (!response.users.length) {
        setSearchEmpty(true);
      } else {
        setSearchEmpty(false);
        setUsers(response.users);
      }

      setResultsOpen(true);
    } catch (error) {
      console.log({ error });
    }

    setSearching(false);
  };

  const findUsersDebounce = _debounce(findUsers, 100, {
    trailing: true,
  });

  useEffect(() => {
    if (inputText) {
      findUsersDebounce();
    }
  }, [inputText]); // eslint-disable-line react-hooks/exhaustive-deps

  const createChannel = async () => {
    if (!selectedUser?.id || !client.userID) return;

    const conversation = client.channel('messaging', client.userID + selectedUser.id, {
      members: [client.userID, selectedUser.id],
    });
    await conversation.watch();
    await conversation.addMembers([client.userID, selectedUser.id]);

    setActiveChannel?.(conversation);
    setSelectedUser(undefined);
    setUsers([]);
    onClose();
  };

  const addUser = (addedUser: UserResponse) => {
    if (selectedUser?.id === addedUser.id) return;

    setSelectedUser(addedUser);
    setResultsOpen(false);
    setInputText('');
    if (inputRef.current) inputRef.current.focus();
  };

  const removeUser = () => {
    setSelectedUser(undefined);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'ArrowUp') {
        setFocusedUser((prev) => {
          if (prev === undefined) return 0;
          return prev === 0 ? users.length - 1 : prev - 1;
        });
      }
      if (event.key === 'ArrowDown') {
        setFocusedUser((prev) => {
          if (prev === undefined) return 0;
          return prev === users.length - 1 ? 0 : prev + 1;
        });
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        if (focusedUser !== undefined) {
          addUser(users[focusedUser]);
          return setFocusedUser(undefined);
        }
        if (selectedUser) {
          createChannel();
        }
      }
    },
    [users, focusedUser, selectedUser] // eslint-disable-line
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="create-channel">
      {/* Header */}
      <div className="create-channel__header">
        <h3 className="create-channel__title">New Conversation</h3>
        <button className="create-channel__close" onClick={onClose} title="Close">
          <CloseOutlined />
        </button>
      </div>

      {/* Search input */}
      <div className="create-channel__search">
        <div className="create-channel__search-icon">
          {searching ? <LoadingOutlined /> : <SearchOutlined />}
        </div>
        {selectedUser ? (
          <div className="create-channel__selected-user" onClick={removeUser}>
            <Avatar image={selectedUser.image} name={selectedUser.name} />
            <span>{selectedUser.name}</span>
            <CloseOutlined className="create-channel__selected-user-remove" />
          </div>
        ) : (
          <input
            autoFocus
            ref={inputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Search for a user..."
            type="text"
            className="create-channel__input"
          />
        )}
      </div>

      {/* Search results */}
      <div className="create-channel__results">
        {inputText && resultsOpen && !!users.length && !searchEmpty && (
          <ul className="create-channel__user-list">
            {users.map((user, i) => (
              <li
                className={`create-channel__user-item ${focusedUser === i ? 'focused' : ''}`}
                onClick={() => addUser(user)}
                key={user.id}
              >
                <Avatar image={user.image} name={user.name} />
                <div className="create-channel__user-info">
                  <span className="create-channel__user-name">{user.name}</span>
                  {user.online && <span className="create-channel__user-online">Online</span>}
                </div>
              </li>
            ))}
          </ul>
        )}

        {inputText && searchEmpty && (
          <div className="create-channel__empty">
            <SearchOutlined style={{ fontSize: 24, color: '#b0b0b0' }} />
            <p>No users found for "{inputText}"</p>
          </div>
        )}

        {!inputText && !selectedUser && (
          <div className="create-channel__empty">
            <SearchOutlined style={{ fontSize: 24, color: '#b0b0b0' }} />
            <p>Search for a user to start a conversation</p>
          </div>
        )}
      </div>

      {/* Footer with action button */}
      {selectedUser && (
        <div className="create-channel__footer">
          <button className="create-channel__start-btn" onClick={createChannel}>
            Start conversation with {selectedUser.name}
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(CreateChannel);
